import {
  Box,
  List,
  ListItem,
  SvgIconProps,
  Typography,
  Stack
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { isNavRouteActive } from "./isNavRouteActive";
import NavigationItems from "../../../navigation/NavigationItems";
import useInterface from "../../../../../setup/store/useInterfaceStore";
import { useAuth } from "../../../../../context/AuthContext";
import {
  NavigationGroup,
  parentNavigations,
  parentTeacherNavigations,
  registrarNavigations,
  studentNavigations,
  teacherNavigations,
} from "../../../../../setup/route_config";
import React, { useEffect, useMemo } from "react";
import { startCase } from "lodash";
import adminNavigations from "../../../../../setup/route_config/adminRoutes";
import { useIsDesktopLayout } from "../../../../../hooks/useIsDesktopLayout";
import useDrawerStore from "../../../../../setup/store/useDrawerStore";
import { useAiFeaturesEnabled } from "../../../../../hooks/useAiFeaturesEnabled";

const Workspaces = () => {
  const { profile: user } = useAuth();
  const { activeTab, setActiveTab } = useInterface();
  // Tutor tabs (requiresAiTutor) are hidden unless the AI-tutor backend is on
  // in this environment — pre-release feature stays out of production.
  const aiTutorEnabled = useAiFeaturesEnabled();

  const isMobile = !useIsDesktopLayout();
  const { toggle } = useDrawerStore();

  const { pathname } = useLocation();

  const spaces = useMemo(() => {
    switch (user?.base_role) {
      case "registrar":
        return registrarNavigations;
      case "parent":
        return parentNavigations;
      case "student":
        return studentNavigations;
      case "teacher":
        return teacherNavigations;
      case "parent-teacher":
        return parentTeacherNavigations;
      case "admin":
        return adminNavigations;
      default:
        return [];
    }
  }, [user]);

  useEffect(() => {
    const current_path = pathname.split("/")[2];
    setActiveTab(current_path);
  }, [pathname, setActiveTab]);

  // Auto-close the mobile drawer only after the user actually navigates
  // (i.e. when the URL pathname changes), not on every activeTab init.
  // The previous version closed the drawer on first mount because
  // setActiveTab always fires after pathname is read, which made the
  // burger-toggle appear to "do nothing" on a fresh mobile load.
  // isMobile is intentionally read but not a dep: closing must key off
  // navigation, not off crossing the breakpoint on resize.
  useEffect(() => {
    if (isMobile) toggle(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  
  
  return (
    <Stack>
      <List sx={{ pt: 0 }}>
        {(spaces as NavigationGroup[]).map((group, ind) => {
          const visible = group.routes.filter(
            (r) => !r.hidden && (!r.requiresAiTutor || aiTutorEnabled),
          );
          if (visible.length === 0) return null;
          return (
            <Box key={ind} mb={2}>
              <ListItem>
                <Typography variant="description" style={{ opacity: 0.5, textTransform: "uppercase" }}>{group.title}</Typography>
              </ListItem>
              {visible.map((route) => (
                <NavigationItems
                  key={route.path}
                  active={isNavRouteActive(route.path, activeTab)}
                  icon={
                    route.icon && React.isValidElement(route.icon)
                      ? React.cloneElement(route.icon as React.ReactElement<SvgIconProps>, {
                          color: isNavRouteActive(route.path, activeTab) ? "primary" : undefined,
                        })
                      : null
                  }
                  label={startCase(route.label)}
                  link={`/${user?.base_role}` + (route?.path != null ? `/${route.path}` : "")}
                />
              ))}
            </Box>
          );
        })}
      </List>
    </Stack>
  );
};

export default Workspaces;
